import { z } from 'zod'

/**
 * The milestones worth marking.
 *
 * Spaced so they keep arriving without ever being routine: often enough early
 * on that a new player reaches one, far enough apart later that a hundredth
 * game still means something. Deliberately about how many, never how well.
 */
export const MILESTONES = [10, 25, 50, 100, 250, 500] as const

export type Milestone = (typeof MILESTONES)[number]

/**
 * The biggest milestone a count has passed.
 *
 * Note "passed", not "reached". Eleven games has passed ten; the account is
 * not waiting for a tenth game that already happened.
 */
export function milestonePassed(total: number): number {
  return MILESTONES.filter((milestone) => milestone <= total).at(-1) ?? 0
}

/**
 * Whether this round crossed one, given what the account had already passed.
 *
 * There was a `milestoneReached` here once, removed on review for having no
 * caller — and it was subtly wrong anyway: it matched on the count being
 * *exactly* a milestone, which celebrates the tenth game and then celebrates
 * it again if a play is ever deleted and another played.
 *
 * Crossing is a change of state instead. The account remembers the biggest one
 * it has passed, and an award happens when that number goes up — which can be
 * said once, cannot be said twice, and stays right even if the count moves in
 * ways nobody planned for.
 */
export function milestoneCrossed(previouslyPassed: number, total: number): Milestone | null {
  const now = milestonePassed(total)
  if (now <= previouslyPassed) return null

  // The one just crossed. If a count somehow jumped several — a batch import,
  // a repair — the newest is the one worth marking, and the others are not
  // owed a queue of celebrations she never earned one at a time.
  return MILESTONES.find((milestone) => milestone === now) ?? null
}

/** The next one to aim at, so a parent can see how close she is. */
export function nextMilestone(total: number): Milestone | null {
  return MILESTONES.find((milestone) => milestone > total) ?? null
}

/**
 * How much somebody has played, as the server counts it.
 *
 * Parsed rather than assumed. This is the same lesson the socket payloads
 * taught: a response whose shape is only asserted is a response that will one
 * day be something else, and a page that trusted it crashed rather than
 * shrugging.
 */
export const playCountSchema = z
  .object({
    total: z.number().int().min(0),
    byGame: z.record(z.string(), z.number().int().min(0)),
  })
  .strict()

export type PlayCount = z.infer<typeof playCountSchema>

/**
 * The award, as it rides back on a recorded play.
 *
 * Loose about the rest of the response on purpose: this is the same body the
 * play itself comes back in, and a schema that insisted on knowing every field
 * would start refusing awards the next time a column is added.
 */
export const milestoneAwardSchema = z
  .object({ milestone: z.number().int().min(1).nullable() })
  .loose()

export type MilestoneAward = z.infer<typeof milestoneAwardSchema>
